import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { RASHIS, getLuckyPicks } from './luckyData'

/**
 * Compact "Daily Lucky Picks" widget for dark bands. Sits in the right half
 * of the contact CTA's two-column grid, so it stacks its own content in a
 * single column. Same deterministic picks as the full tool on /astro-tools.
 * Tailwind-only on purpose: no inline gradient strings for the editor's
 * class-rewriter to corrupt.
 */
export default function LuckyPicksWidget() {
  const [sign, setSign] = useState('Aries')
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const data = useMemo(() => getLuckyPicks(sign, today), [sign, today])

  const tiles = [
    { icon: '🔢', label: 'Lucky No.', value: data.number },
    { icon: '🎨', label: 'Colour', value: data.colour },
    { icon: '💎', label: 'Gemstone', value: data.gemstone },
    { icon: '🧭', label: 'Direction', value: data.direction },
  ]

  return (
    <div className="rounded-[26px] border border-white/15 bg-black/25 backdrop-blur-sm p-6 sm:p-8 text-left">
      <div className="flex items-center gap-2">
        <span className="text-lg" aria-hidden="true">🍀</span>
        <h3 className="font-royal text-[10px] uppercase font-bold tracking-[.4em] text-goldPrimary/85">
          Daily Lucky Picks
        </h3>
      </div>
      <p className="text-[12px] text-white/50 font-light mt-2">
        Tap your zodiac sign and see today&apos;s cosmic fortune.
      </p>

      <div className="flex flex-wrap gap-1.5 mt-4">
        {RASHIS.map((r) => {
          const active = r.en === sign
          return (
            <button
              key={r.en}
              type="button"
              onClick={() => setSign(r.en)}
              aria-pressed={active}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10.5px] transition-colors ${
                active
                  ? 'bg-goldPrimary border-goldPrimary text-astroDark font-bold'
                  : 'border-white/20 text-white/60 hover:border-goldPrimary/60 hover:text-goldPrimary'
              }`}
            >
              <span aria-hidden="true">{r.glyph}</span>
              {r.en}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-5">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-2xl border border-white/10 bg-white/5 px-2.5 py-3">
            <span className="text-lg" aria-hidden="true">{t.icon}</span>
            <p className="text-[8.5px] uppercase tracking-[.2em] font-semibold text-white/40 mt-1">{t.label}</p>
            <p className="text-[13px] text-goldPrimary font-semibold mt-0.5">{t.value}</p>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-white/45 font-light mt-4">
        Metal: <span className="text-white/70">{data.metal}</span> · Lucky Hour:{' '}
        <span className="text-white/70">{data.time}</span> · Mantra:{' '}
        <span className="text-white/70">{data.mantra}</span>
      </p>

      <Link
        to="/astro-tools"
        className="inline-block mt-3 text-[10px] uppercase tracking-[.25em] font-bold text-goldPrimary/80 hover:text-goldPrimary transition-colors"
      >
        Explore All Free Astro Tools →
      </Link>
    </div>
  )
}
